import { toast } from 'components/ui/toast-store'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import RightPanel from 'components/RightPanel/RightPanel'
import { useDispatch, useSelector } from 'react-redux'
import { FileText, Upload } from 'lucide-react'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import SidebarPartners from 'components/Sidebars/SidebarPartners'
import SidebarRecentActivity from 'components/Sidebars/SidebarRecentActivity'
import { Button } from 'components/ui/button'
import { clearError } from 'slices/documentLibrarySlice'
import {
	DocumentLibraryList,
	UploadFilesDialog,
	VersionHistoryDialog,
	DocumentPreviewDialog,
	DocumentShareDialog,
	DocumentEditSheet,
	getCurrentVersion,
	matchesScope,
	type DocumentLibraryState,
	type LibraryDocument,
	type LibraryScopeArgs,
} from 'features/DocumentLibrary'

/**
 * Cross-scope Document Library rollup, mounted at
 * `/admin/library/documents`. Lists every libraryDocument the current
 * user has read access to — owner OR partner across projects and
 * portfolios, plus global when the user is admin. Deduplicates by
 * `libraryName` so the same file uploaded to multiple parents shows
 * as ONE row (the most-recently-uploaded instance).
 *
 * MYD-299 originally shipped this URL as an admin-gated workspace
 * library. MYD-303 first repointed it to the per-user uploads view
 * and then expanded to the access-aware cross-scope union the user
 * was actually expecting.
 */
const MyLibraryDocumentsCN: React.FC = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	const { userInfo } = useSelector((state: any) => state.userLogin)
	const userId: string | undefined = userInfo?._id

	const scopeArgs: LibraryScopeArgs | null = userId
		? { scope: 'my-library', userId }
		: null

	const sliceError = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			state.libraryDocuments.error,
	)

	// Notifications go through the shared toast store (MYD-561), titled as before.
	const showToast = ({ message }: { message: string }) =>
		toast.error(message, { title: 'Something went wrong' })

	useEffect(() => {
		if (sliceError) {
			showToast({ message: sliceError })
			dispatch(clearError())
		}
	}, [sliceError, dispatch])

	// Upload dialog's collision detection: this screen's upload CTA
	// targets the global scope (no parent project/portfolio context
	// here), so the collision check needs the global-scope docs in
	// the slice, not the my-library response. If the user hasn't
	// fetched the global scope yet, this will be empty and the
	// backend's 409 on duplicate name is the fallback.
	const existingDocuments = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) => {
			const { entities, ids } = state.libraryDocuments
			return ids
				.map(_id => entities[_id])
				.filter(
					(d): d is LibraryDocument =>
						!!d && matchesScope(d, { scope: 'global' }) && !d.isDeleted,
				)
				.map(d => ({ id: d._id, libraryName: d.libraryName }))
		},
	)

	// Recent Activity feeds off the documents this screen has already loaded,
	// so the rightbar costs no fetch of its own and follows an upload, rename
	// or delete without being told.
	const { entities: docEntities, ids: docIds, isLoading: docsLoading } = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) => state.libraryDocuments,
	)

	const recentDocuments = useMemo(
		() =>
			docIds
				.map(_id => docEntities[_id])
				.filter((d): d is LibraryDocument => !!d && !d.isDeleted)
				.map(d => {
					const version = getCurrentVersion(d)
					return {
						id: d._id,
						name: d.libraryName,
						detail: version?.originalFilename ?? null,
						// The current version's upload is when the document last
						// actually changed; updatedAt also moves on a rename.
						timestamp: version?.uploadedAt || d.updatedAt || d.createdAt,
						imageUrl: version?.thumbnailUrl ?? null,
						icon: <FileText className="w-3.5 h-3.5" />,
					}
				}),
		[docIds, docEntities],
	)

	const [uploadOpen, setUploadOpen] = useState(false)
	const [versionsDocId, setVersionsDocId] = useState<string | null>(null)
	const [previewDocId, setPreviewDocId] = useState<string | null>(null)
	const [shareTarget, setShareTarget] = useState<
		{ documentId: string; versionId?: string } | null
	>(null)
	const [editDocId, setEditDocId] = useState<string | null>(null)

	const openUpload = useCallback(() => setUploadOpen(true), [])

	const handleShare = useCallback(
		(documentId: string, versionId?: string) => {
			setShareTarget({ documentId, versionId })
		},
		[],
	)

	const handleEditUploadNewVersion = useCallback(() => {
		setEditDocId(null)
		setUploadOpen(true)
	}, [])

	const headerSubtitle = useMemo(
		() =>
			'Every document on a project or portfolio you have access to, plus the admin library. Same file in multiple places shows once — open a row to preview, version, share, or rename.',
		[],
	)

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN
				mobileOpen={mobileMenuOpen}
				onMobileClose={() => setMobileMenuOpen(false)}
			/>

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN
					onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
				/>

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						{!scopeArgs ? null : (
							<div className="max-w-[1080px] mx-auto w-full">
								<div className="flex flex-wrap justify-between items-center gap-4 mb-6">
									<div className="flex flex-col gap-1">
										<h2 className="text-2xl font-bold m-0">My Documents</h2>
										<span className="text-sm text-gray-600">
											{headerSubtitle}
										</span>
									</div>
									{/*
										Upload from here lands in the admin / global scope —
										there's no parent project/portfolio context on this
										screen. Most uploads will happen from a project or
										portfolio Documents tab; this CTA is a fallback.
									*/}
									<Button onClick={openUpload}>
										<Upload className="w-4 h-4 mr-1" />
										<span>Upload Document</span>
									</Button>
								</div>

								<DocumentLibraryList
									{...scopeArgs}
									onPreview={setPreviewDocId}
									onShowVersions={setVersionsDocId}
									onShare={handleShare}
									onRowClick={setEditDocId}
									onUpload={openUpload}
								/>

								{/*
									Upload dialog runs at the global scope: a doc uploaded
									from this screen has no project/portfolio parent. To
									upload into a specific project/portfolio, the user
									goes to that surface's Documents tab.
								*/}
								<UploadFilesDialog
									open={uploadOpen}
									onClose={() => setUploadOpen(false)}
									scopeArgs={{ scope: 'global' }}
									existingDocuments={existingDocuments}
								/>

								<VersionHistoryDialog
									open={!!versionsDocId}
									onClose={() => setVersionsDocId(null)}
									documentId={versionsDocId}
									onShareVersion={handleShare}
								/>

								<DocumentPreviewDialog
									open={!!previewDocId}
									onClose={() => setPreviewDocId(null)}
									documentId={previewDocId}
								/>

								<DocumentShareDialog
									open={!!shareTarget}
									onClose={() => setShareTarget(null)}
									documentId={shareTarget?.documentId ?? null}
									versionId={shareTarget?.versionId}
								/>

								<DocumentEditSheet
									open={!!editDocId}
									onClose={() => setEditDocId(null)}
									documentId={editDocId}
									onUploadNewVersion={handleEditUploadNewVersion}
									onShare={handleShare}
									onShowVersions={setVersionsDocId}
								/>

							</div>
						)}
					</main>

					<RightPanel storageKey="library-documents" label="Library panel">
						<div className="flex flex-col h-1/2 border-b border-gray-200">
							<SidebarRecentActivity
								items={recentDocuments}
								isLoading={docsLoading}
								emptyMessage="No documents yet"
								emptyHint="Upload a document to see it here."
								onSelect={item => setEditDocId(item.id)}
							/>
						</div>
						<div className="flex flex-col h-1/2">
							<SidebarPartners />
						</div>
					</RightPanel>
				</div>
			</div>
		</div>
	)
}

export default MyLibraryDocumentsCN
